/**
 * codiceFiscale.js — validazione del codice fiscale.
 *
 * Due livelli:
 *  1. formato + carattere di controllo (checksum) — intercetta i typo
 *  2. coerenza con data di nascita e sesso gia' inseriti — intercetta
 *     il CF valido ma abbinato ai dati sbagliati
 *
 * Non verifica il comune (codice catastale): scelta deliberata per non
 * trascinare la tabella dei ~8.000 codici Belfiore.
 */

const PARI = {
    0: 0, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9,
    A: 0, B: 1, C: 2, D: 3, E: 4, F: 5, G: 6, H: 7, I: 8, J: 9,
    K: 10, L: 11, M: 12, N: 13, O: 14, P: 15, Q: 16, R: 17, S: 18,
    T: 19, U: 20, V: 21, W: 22, X: 23, Y: 24, Z: 25
};

const DISPARI = {
    0: 1, 1: 0, 2: 5, 3: 7, 4: 9, 5: 13, 6: 15, 7: 17, 8: 19, 9: 21,
    A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21,
    K: 2, L: 4, M: 18, N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14,
    U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23
};

const RESTO = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// Mese: lettera -> numero (0-based)
const MESI = { A: 0, B: 1, C: 2, D: 3, E: 4, H: 5, L: 6, M: 7, P: 8, R: 9, S: 10, T: 11 };

const FORMATO = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/;

/**
 * Carattere di controllo atteso per le prime 15 posizioni.
 */
function carattereControllo(cf15) {
    let somma = 0;
    for (let i = 0; i < 15; i++) {
        const c = cf15[i];
        somma += (i % 2 === 0) ? DISPARI[c] : PARI[c];
    }
    return RESTO[somma % 26];
}

/**
 * Estrae giorno, mese, anno (2 cifre) e sesso dal CF.
 * Nel CF il giorno della donna e' aumentato di 40.
 */
function datiDalCF(cf) {
    const anno = parseInt(cf.substring(6, 8), 10);
    const mese = MESI[cf[8]];
    let giorno = parseInt(cf.substring(9, 11), 10);
    const sesso = giorno > 40 ? 'F' : 'M';
    if (giorno > 40) giorno -= 40;
    return { anno, mese, giorno, sesso };
}

/**
 * Valida il codice fiscale.
 *
 * @param {string} cf
 * @param {object} [anagrafica]  { data_nascita: 'YYYY-MM-DD', sesso: 'M'|'F' }
 * @returns {{ valido: boolean, errore: string|null, avvisi: string[] }}
 */
function validaCodiceFiscale(cf, anagrafica = {}) {
    const avvisi = [];
    const codice = String(cf || '').toUpperCase().replace(/\s/g, '');

    if (!codice) return { valido: false, errore: 'Codice fiscale mancante', avvisi };
    if (codice.length !== 16) return { valido: false, errore: 'Il codice fiscale deve essere di 16 caratteri', avvisi };
    if (!FORMATO.test(codice)) return { valido: false, errore: 'Formato del codice fiscale non valido', avvisi };

    // 1. Carattere di controllo
    const atteso = carattereControllo(codice.substring(0, 15));
    if (atteso !== codice[15]) {
        return { valido: false, errore: 'Codice fiscale non valido: carattere di controllo errato', avvisi };
    }

    // 2. Coerenza con i dati anagrafici, se disponibili
    const dati = datiDalCF(codice);

    if (dati.mese === undefined) {
        return { valido: false, errore: 'Codice fiscale non valido: mese di nascita non riconosciuto', avvisi };
    }

    if (anagrafica.sesso && (anagrafica.sesso === 'M' || anagrafica.sesso === 'F')) {
        if (anagrafica.sesso !== dati.sesso) {
            return {
                valido: false,
                errore: `Il codice fiscale indica sesso ${dati.sesso}, ma è stato selezionato ${anagrafica.sesso}`,
                avvisi
            };
        }
    }

    if (anagrafica.data_nascita) {
        const d = new Date(anagrafica.data_nascita);
        if (!isNaN(d)) {
            const giornoOK = d.getDate() === dati.giorno;
            const meseOK = d.getMonth() === dati.mese;
            const annoOK = (d.getFullYear() % 100) === dati.anno;

            if (!giornoOK || !meseOK || !annoOK) {
                return {
                    valido: false,
                    errore: 'Il codice fiscale non è coerente con la data di nascita indicata',
                    avvisi
                };
            }
        }
    }

    return { valido: true, errore: null, avvisi };
}


/**
 * Valida il documento identificativo secondo il tipo.
 *
 * @param {string} codice
 * @param {string} tipo        cf_italiano | iss_san_marino | estero
 * @param {object} anagrafica  { data_nascita, sesso }
 */
function validaDocumento(codice, tipo = 'cf_italiano', anagrafica = {}) {
    const c = String(codice || '').toUpperCase().replace(/\s/g, '');

    if (tipo === 'iss_san_marino') {
        if (!c) return { valido: false, errore: 'Codice ISS mancante', avvisi: [] };
        if (!/^\d{1,7}$/.test(c)) {
            return {
                valido: false,
                errore: 'Il codice ISS di San Marino è composto da un massimo di sette cifre',
                avvisi: []
            };
        }
        return {
            valido: true,
            errore: null,
            avvisi: ['Le prestazioni di questo paziente non vanno trasmesse al Sistema TS.']
        };
    }

    if (tipo === 'estero') {
        if (!c) return { valido: false, errore: 'Documento identificativo mancante', avvisi: [] };
        return {
            valido: true,
            errore: null,
            avvisi: ['Le prestazioni di questo paziente non vanno trasmesse al Sistema TS.']
        };
    }

    return validaCodiceFiscale(c, anagrafica);
}

/**
 * Codice catastale del comune di nascita: caratteri 12-15.
 *
 * Restituisce null per chi e' nato all'estero (codice che inizia per
 * Z), che non e' nella tabella dei comuni e non va verificato.
 */
function codiceCatastaleDaCF(cf) {
    const c = String(cf || '').toUpperCase().replace(/\s/g, '');
    if (c.length !== 16) return null;
    const cat = c.substring(11, 15);
    if (cat.startsWith('Z')) return null;
    return cat;
}

export { validaCodiceFiscale, carattereControllo, validaDocumento, codiceCatastaleDaCF };
export default validaCodiceFiscale;
